import { Controller } from "@hotwired/stimulus"

// Milliseconds a member's brand holds, and the wipe that brings in the next.
const HOLD = 1700
const WIPE = 1100
// The hero's trace curve, not --public-ease: front-loaded, a wipe snaps
// instead of sweeping.
const EASE = "cubic-bezier(0.45, 0, 0.2, 1)"

// Connects to data-controller="public--rebrand"
//
// The branding band. The server draws the first member's cover and post on
// top of the other two sets, which no JavaScript, reduced motion and a Turbo
// cache restore all show. While the desk is on screen, each set in turn
// wipes in over the last behind a soft edge, and its member lights in the
// list, whose line fills for as long as the set stays. Choosing a member
// shows their set and ends the cycle for good. The pins on the cover step
// aside while a wipe runs and settle back in when it lands.
export default class extends Controller {
  static targets = ["stage", "set", "member"]

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.current = 0
    this.element.style.setProperty("--rebrand-stay", `${WIPE + HOLD}ms`)
    this.motion = window.matchMedia("(prefers-reduced-motion: no-preference)")
    this.cycle = this.cycle.bind(this)
    this.reset = this.reset.bind(this)
    this.motion.addEventListener("change", this.cycle)
    document.addEventListener("visibilitychange", this.cycle)
    document.addEventListener("turbo:before-cache", this.reset)
    this.observer = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting
      this.cycle()
    }, { threshold: 0.5 })
    this.observer.observe(this.stageTarget)
  }

  disconnect() {
    if (!this.observer) return

    this.observer.disconnect()
    this.motion.removeEventListener("change", this.cycle)
    document.removeEventListener("visibilitychange", this.cycle)
    document.removeEventListener("turbo:before-cache", this.reset)
    this.reset()
  }

  choose({ currentTarget }) {
    this.chosen = true
    this.show(this.memberTargets.indexOf(currentTarget))
  }

  // The clock runs only while the desk is on screen, the tab is visible,
  // motion is welcome and nobody has chosen a member.
  cycle() {
    clearTimeout(this.timer)
    const running = this.inView && !document.hidden && this.motion.matches && !this.chosen
    this.element.classList.toggle("is-cycling", running)
    if (running) this.timer = setTimeout(() => this.show((this.current + 1) % this.setTargets.length), WIPE + HOLD)
  }

  show(index) {
    if (index === this.current) return this.cycle()

    this.land()
    const leaving = this.setTargets[this.current]
    const arriving = this.setTargets[index]
    this.current = index
    this.mark()
    arriving.classList.add("is-arriving")
    this.stageTarget.classList.remove("is-landed")
    this.settle = () => {
      leaving.classList.remove("is-current")
      arriving.classList.replace("is-arriving", "is-current")
      this.stageTarget.style.setProperty("--pin-logo-x", arriving.dataset.logoEnds)
    }
    if (this.motion.matches) {
      this.wipe = arriving.animate([{ maskPosition: "100% 0" }, { maskPosition: "0% 0" }], { duration: WIPE, easing: EASE })
      this.wipe.onfinish = () => this.land()
    } else {
      this.land()
    }
    this.cycle()
  }

  // Ends the wipe in flight, if any, with its set in place.
  land() {
    this.wipe?.cancel()
    this.wipe = null
    this.settle?.()
    this.settle = null
    this.stageTarget.classList.add("is-landed")
  }

  mark() {
    this.memberTargets.forEach((member, index) => member.setAttribute("aria-pressed", String(index === this.current)))
    this.setTargets.forEach((set, index) => set.setAttribute("aria-hidden", String(index !== this.current)))
  }

  reset() {
    clearTimeout(this.timer)
    this.land()
    this.chosen = false
    this.current = 0
    this.setTargets.forEach((set, index) => set.classList.toggle("is-current", index === 0))
    this.stageTarget.style.setProperty("--pin-logo-x", this.setTargets[0].dataset.logoEnds)
    this.mark()
    this.element.classList.remove("is-cycling")
  }
}
